import React, { FC, useState } from 'react';
import Modal from '@common/modal';
import {
  useApplyTermsCheckNew,
  useTermsAgreeForSn,
  applyTermsCheckNewKeys,
} from '@repository/userTermsAgrees/useUserTermsAgrees';
import { useTermsOne } from '@repository/terms/useTerms';
import { useUserInfo } from '@repository/auth/useUserInfo';
import Term from './Term';
import TermsDto from '@domain/rs/terms/TermsRs';
import { phsTestTermsSortMap } from '@domain/constant/v1/termsType';
import useDebounce from '@utils/hooks/useDebounce';
import { queryClient } from '@utils/queryClient';

import classnames from 'classnames/bind';
import styles from './testTerms.module.scss';
const cx = classnames.bind(styles);

const getInitialState = (status = false) => {
  return Array.from({ length: Object.keys(phsTestTermsSortMap).length }, () => status);
};

interface ITermListProps {
  termList: TermsDto[];
  checkStatus: boolean[];
  onCheck: (args: { idx: number }) => void;
  openStatus: boolean[];
  onOpen: (args: { idx: number }) => void;
}

const TermList: FC<ITermListProps> = ({ termList, checkStatus, onCheck, openStatus, onOpen }) => {
  return (
    <>
      {termList.map((term, idx) => (
        <div key={term.sn} className={cx('termsWrap')}>
          <Term
            isChecked={checkStatus[idx]}
            {...term}
            onClick={() => onCheck({ idx })}
            isOpen={openStatus[idx]}
            onOpen={() => onOpen({ idx })}
          />
        </div>
      ))}
    </>
  );
};

interface ITestTermsModalProps {
  visible: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const TestTermsModal: FC<ITestTermsModalProps> = ({ visible, onConfirm, onClose }) => {
  const { data: userData } = useUserInfo();
  const isLogin = !!userData?.id;

  const applyTermsData = useApplyTermsCheckNew(isLogin);
  const { data: copyrightProtectionData } = useTermsOne(applyTermsData[0]?.data?.termsSn, applyTermsData[0].isFetched);
  const { data: privacyPolicyData } = useTermsOne(applyTermsData[1]?.data?.termsSn, applyTermsData[1].isFetched);

  const { mutateAsync: termsAgreeMutateAsync } = useTermsAgreeForSn();

  const [checkStatus, setCheckStatus] = useState(getInitialState());

  const getCheckStatus = (status: boolean[]) => status.every((state) => state !== false);

  const onSubmit = useDebounce(async () => {
    if (!getCheckStatus(checkStatus)) return;
    if (!copyrightProtectionData || !privacyPolicyData) return;

    //TODO 추후 동의문 한번에 요청하는 API 교체 필요
    const copyrightProtectionResult = await termsAgreeMutateAsync(copyrightProtectionData.sn);
    const privacyPolicyResult = await termsAgreeMutateAsync(privacyPolicyData.sn);

    if (copyrightProtectionResult && privacyPolicyResult) {
      await queryClient.refetchQueries(applyTermsCheckNewKeys, { active: true });
      onConfirm();
    }
  }, 300);

  const onClickAll = () => {
    setCheckStatus(getInitialState(!getCheckStatus(checkStatus)));
  };

  const onCheck = ({ idx }: { idx: number }) => {
    setCheckStatus((prevCheckStatus) => {
      const postCheckStatus = prevCheckStatus;
      postCheckStatus[idx] = !prevCheckStatus[idx];
      return [...postCheckStatus];
    });
  };

  // 약관 open/close 관련 작업
  const [openStatus, setOpenStatus] = useState(getInitialState());
  const getOpenStatus = (status: boolean[]) => status.every(() => false);
  const onOpen = ({ idx }: { idx: number }) => {
    const postOpenStatus = getInitialState(getOpenStatus(openStatus));

    postOpenStatus[idx] = !openStatus[idx];
    setOpenStatus(postOpenStatus);
  };

  if (!copyrightProtectionData || !privacyPolicyData) return null;

  return (
    <Modal visible={visible} dimmed={true}>
      <div className={cx('layerTerms')}>
        <strong className={cx('title')}>
          개발자 검사 응시를 위해
          <br />
          아래의 항목에 동의해 주세요.
        </strong>

        <div className={cx('termsArea')}>
          <div className={cx('termsWrap', 'all')}>
            <div className={cx('labelWrap')}>
              <label className={cx('label')}>
                <input
                  type="checkbox"
                  className={cx('input', 'hidden')}
                  checked={getCheckStatus(checkStatus)}
                  onChange={onClickAll}
                />
                <span className={cx('mark')} />
                아래의 사항에 대해 전체 동의합니다.
              </label>
            </div>
          </div>

          <TermList
            termList={[privacyPolicyData, copyrightProtectionData]}
            checkStatus={checkStatus}
            onCheck={onCheck}
            openStatus={openStatus}
            onOpen={onOpen}
          />
        </div>

        <button className={cx('btnAgreement')} type="button" onClick={onSubmit} disabled={!getCheckStatus(checkStatus)}>
          동의하고 시작하기
        </button>

        <a className={cx('btnClose')} role="button" onClick={onClose}>
          <span className={cx('hidden')}>close</span>
        </a>
      </div>
    </Modal>
  );
};

export default TestTermsModal;
